import { Popover, PopoverPosition } from "@amzn/stencil-react-components/dist/submodules/popover";
import { Row } from "@amzn/stencil-react-components/layout";
import { StarRatingDisplay, StarRatingDisplaySize } from "@amzn/stencil-react-components/star-rating";
import { Text } from "@amzn/stencil-react-components/text";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { config } from "config";
import React from "react";
import { FunctionComponent } from "react";

export enum StarAverageRatingSize {
  ExtraSmall,
  Small,
  Medium,
}

export interface StarAverageRatingProps {
  size: StarAverageRatingSize;
  averageRating?: number;
  hideScore?: boolean;
  touchpoint?: string;
  voteNumber?: number;
  popoverContent?: JSX.Element;
  popoverPosition?: PopoverPosition;
}

const getSizes = (size: StarAverageRatingSize) => {
  switch (size) {
    case StarAverageRatingSize.ExtraSmall:
      return { starRating: StarRatingDisplaySize.ExtraSmall, fontSize: "T100" };
    case StarAverageRatingSize.Small:
      return { starRating: StarRatingDisplaySize.Small, fontSize: "T200" };
    case StarAverageRatingSize.Medium:
      return { starRating: StarRatingDisplaySize.Medium, fontSize: "T100" };
    default:
      return { starRating: StarRatingDisplaySize.Small, fontSize: "T200" };
  }
};

const NEUTRAL_60_COLOR = "#56616C";

export const StarAverageRating: FunctionComponent<StarAverageRatingProps> = ({
  size,
  averageRating,
  hideScore = false,
  voteNumber,
  popoverContent,
  popoverPosition = PopoverPosition.TopTrailing,
  touchpoint,
}) => {
  const sizes = getSizes(size);
  const averageRatingFixed = parseFloat(averageRating?.toFixed(1) ?? "0");
  const AverageRatingComponent = () => (
    <StarRatingDisplay value={averageRatingFixed} size={sizes.starRating} dataTestId="star-rating-display" />
  );
  const component: JSX.Element = (
    <Row alignItems="center" columnGap={"8px"} dataTestId="star-average-rating">
      {!hideScore && (
        <Text
          fontSize={sizes.fontSize}
          margin={"4px"}
          dataTestId="star-average-rating-average"
          color={NEUTRAL_60_COLOR}
        >
          {averageRating ? averageRatingFixed : "No ratings yet"}
        </Text>
      )}
      {averageRating && <AverageRatingComponent />}
      {voteNumber != null && (averageRating ?? 0) > 0 && (
        <Text
          fontSize={sizes.fontSize}
          margin={"4px"}
          dataTestId="star-average-rating-vote-number"
          color={NEUTRAL_60_COLOR}
        >
          ({voteNumber})
        </Text>
      )}
    </Row>
  );

  //should show a popover with custom content
  if (popoverContent) {
    return (
      <Popover
        triggerText={component}
        triggerButtonProps={{
          style: { padding: 0 },
          onClickCapture: () =>
            trackCustomEvent(
              { action: "open" },
              touchpoint ?? "star-average-rating-popover",
              config.analytics.uxmAppId
            ),
        }}
        position={popoverPosition}
        dataTestId="star-average-rating-popover"
      >
        {() => popoverContent}
      </Popover>
    );
  }

  return component;
};
